import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Share2, ArrowRight, MessageSquare, Calendar, Users, Eye,
  Bookmark, BookMarked, Search, Loader2, Inbox, X, Trash2,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSharing, type SharedByMeItem, type SharedWithMeItem } from '../hooks/useSharing';
import { CURRENT_USER } from '../data/dummyData';

type TabKey = 'shared_by_me' | 'shared_with_me';

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Ahora';
  if (mins < 60) return `Hace ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Hace ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Ayer';
  if (days < 7) return `Hace ${days} días`;
  return new Date(iso).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' });
}

function getInitials(nombre: string, apellido: string) {
  return ((nombre?.[0] || '') + (apellido?.[0] || '')).toUpperCase() || '??';
}

export default function SharedPage() {
  const { user, hasPermission } = useAuth();
  const userId = user?.id || CURRENT_USER.id;
  const can = {
    byMe: hasPermission('compartidos.ver_propios'),
    withMe: hasPermission('compartidos.ver_recibidos'),
    search: hasPermission('compartidos.buscar'),
    favorite: hasPermission('compartidos.favorito'),
    detail: hasPermission('compartidos.ver_detalle'),
  };
  const navigate = useNavigate();

  const {
    sharedByMe, sharedWithMe, loading,
    loadSharedByMe, loadSharedWithMe,
    toggleShareFavorite, revokeShare,
  } = useSharing(userId);

  const [tab, setTab] = useState<TabKey>('shared_by_me');
  const [search, setSearch] = useState('');
  const [revokeConfirm, setRevokeConfirm] = useState<{ convId: string; recipientId: string; name: string } | null>(null);

  useEffect(() => {
    loadSharedByMe();
    loadSharedWithMe();
  }, [loadSharedByMe, loadSharedWithMe]);

  const filteredByMe = sharedByMe.filter(s =>
    s.titulo.toLowerCase().includes(search.toLowerCase())
  );
  const filteredWithMe = sharedWithMe.filter(s =>
    s.titulo.toLowerCase().includes(search.toLowerCase()) ||
    `${s.owner_nombre} ${s.owner_apellido}`.toLowerCase().includes(search.toLowerCase())
  );
  const currentList = tab === 'shared_by_me' ? filteredByMe : filteredWithMe;

  function handleViewConversation(conversationId: string) {
    navigate('/chat', { state: { conversationId, fromShared: true } });
  }

  async function handleToggleFavorite(conversationId: string) {
    await toggleShareFavorite(conversationId);
  }

  async function handleRevoke(convId: string, recipientId: string) {
    await revokeShare(convId, recipientId);
    setRevokeConfirm(null);
    loadSharedByMe();
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Análisis Compartidos</h1>
        <p className="text-sm text-gray-500 mt-1">Colabora con tu equipo compartiendo sesiones de análisis del agente.</p>
      </div>

      {/* Tabs + Search */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
          {can.byMe && (
            <button
              onClick={() => setTab('shared_by_me')}
              className={`px-4 py-2 text-xs font-medium rounded-md transition-all flex items-center gap-2 ${
                tab === 'shared_by_me' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Share2 className="w-3.5 h-3.5" /> Compartidos por mí ({sharedByMe.length})
            </button>
          )}
          {can.withMe && (
            <button
              onClick={() => setTab('shared_with_me')}
              className={`px-4 py-2 text-xs font-medium rounded-md transition-all flex items-center gap-2 ${
                tab === 'shared_with_me' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Users className="w-3.5 h-3.5" /> Compartidos conmigo ({sharedWithMe.length})
            </button>
          )}
        </div>

        {can.search && (
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar por título o autor..."
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent"
              style={{ '--tw-ring-color': '#EDAA00' } as React.CSSProperties}
            />
          </div>
        )}
      </div>

      {/* Content */}
      {loading && currentList.length === 0 ? (
        <div className="text-center py-16">
          <Loader2 className="w-8 h-8 text-gray-300 mx-auto mb-3 animate-spin" />
          <p className="text-sm text-gray-500">Cargando análisis compartidos...</p>
        </div>
      ) : currentList.length === 0 ? (
        <div className="text-center py-16">
          <Inbox className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">
            {search
              ? 'No se encontraron resultados para tu búsqueda.'
              : tab === 'shared_by_me'
                ? 'Aún no has compartido ningún análisis. Usa el botón de compartir en el agente para empezar.'
                : 'Nadie ha compartido análisis contigo todavía.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {tab === 'shared_by_me'
            ? (filteredByMe as SharedByMeItem[]).map((session, i) => (
                <SharedByMeCard
                  key={session.id}
                  session={session}
                  index={i}
                  onView={() => handleViewConversation(session.id)}
                  onRevoke={(rid, name) => setRevokeConfirm({ convId: session.id, recipientId: rid, name })}
                />
              ))
            : (filteredWithMe as SharedWithMeItem[]).map((session, i) => (
                <SharedWithMeCard
                  key={session.id + '-' + session.owner_id}
                  session={session}
                  index={i}
                  onView={() => handleViewConversation(session.id)}
                  onToggleFavorite={() => handleToggleFavorite(session.id)}
                />
              ))}
        </div>
      )}

      {/* Revoke confirmation modal */}
      <AnimatePresence>
        {revokeConfirm && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setRevokeConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-sm shadow-2xl"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <div className="p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
                  <X className="w-6 h-6 text-red-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Revocar acceso</h3>
                <p className="text-sm text-gray-500 mb-6">
                  ¿Deseas quitar el acceso compartido a <strong>{revokeConfirm.name}</strong>?
                </p>
                <div className="flex gap-3 justify-center">
                  <button onClick={() => setRevokeConfirm(null)}
                    className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg border border-gray-200">
                    Cancelar
                  </button>
                  <button onClick={() => handleRevoke(revokeConfirm.convId, revokeConfirm.recipientId)}
                    className="px-4 py-2 text-sm text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors">
                    Revocar
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SharedByMeCard({
  session, index, onView, onRevoke,
}: {
  session: SharedByMeItem;
  index: number;
  onView: () => void;
  onRevoke: (recipientId: string, name: string) => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="bg-white rounded-xl border border-gray-100 p-5 hover:shadow-md transition-all group"
      style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
    >
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 flex-1 pr-2">{session.titulo}</h3>
      </div>

      <p className="text-xs text-gray-500 line-clamp-2 mb-4">{session.preview || 'Sin vista previa'}</p>

      <div className="flex items-center gap-4 text-[11px] text-gray-400 mb-4">
        <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {session.message_count} msgs</span>
        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {timeAgo(session.fecha_actualizacion)}</span>
      </div>

      {session.shared_with && session.shared_with.length > 0 && (
        <div className="pt-3 border-t border-gray-50">
          <span className="text-[10px] text-gray-400 mb-2 block">Compartido con:</span>
          <div className="space-y-1.5">
            {session.shared_with.map(s => (
              <div key={s.recipient_id} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full border-2 border-white flex items-center justify-center text-[9px] font-semibold text-white"
                    style={{ backgroundColor: '#1B2A4A' }}
                    title={`${s.nombre} ${s.apellido}`}>
                    {getInitials(s.nombre, s.apellido)}
                  </div>
                  <span className="text-[11px] text-gray-600">{s.nombre} {s.apellido}</span>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); onRevoke(s.recipient_id, `${s.nombre} ${s.apellido}`); }}
                  className="p-1 rounded hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all"
                  title="Revocar acceso"
                >
                  <Trash2 className="w-3 h-3 text-gray-300 hover:text-red-500" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={onView}
        className="mt-3 w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-50 transition-all opacity-0 group-hover:opacity-100"
      >
        <Eye className="w-3.5 h-3.5" /> Ver análisis completo <ArrowRight className="w-3 h-3" />
      </button>
    </motion.div>
  );
}

function SharedWithMeCard({
  session, index, onView, onToggleFavorite,
}: {
  session: SharedWithMeItem;
  index: number;
  onView: () => void;
  onToggleFavorite: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="bg-white rounded-xl border border-gray-100 p-5 hover:shadow-md transition-all group cursor-pointer"
      style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
    >
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 flex-1 pr-2">{session.titulo}</h3>
        <button onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }} className="p-1 rounded hover:bg-gray-50 flex-shrink-0">
          {session.share_favorita
            ? <BookMarked className="w-4 h-4 text-yellow-500" />
            : <Bookmark className="w-4 h-4 text-gray-300" />}
        </button>
      </div>

      <p className="text-xs text-gray-500 line-clamp-2 mb-4">{session.preview || 'Sin vista previa'}</p>

      <div className="flex items-center gap-4 text-[11px] text-gray-400 mb-4">
        <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {session.message_count} msgs</span>
        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {timeAgo(session.shared_at)}</span>
      </div>

      <div className="flex items-center gap-2 pt-3 border-t border-gray-50">
        <span className="text-[10px] text-gray-400">Compartido por:</span>
        <div className="flex items-center gap-1.5">
          <div className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-semibold text-white"
            style={{ backgroundColor: '#1B2A4A' }}>
            {getInitials(session.owner_nombre, session.owner_apellido)}
          </div>
          <span className="text-[11px] text-gray-600 font-medium">
            {session.owner_nombre} {session.owner_apellido}
          </span>
        </div>
      </div>

      <button
        onClick={onView}
        className="mt-3 w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-50 transition-all opacity-0 group-hover:opacity-100"
      >
        <Eye className="w-3.5 h-3.5" /> Ver análisis completo <ArrowRight className="w-3 h-3" />
      </button>
    </motion.div>
  );
}
